import { api } from '@gik/core/api';
import { IAddEditInkindUserUpdatesForm } from '@gik/inkind-page/components/AddEditInkindUserUpdatesModal/AddEditInkindUserUpdatesModal';
import { moment } from '@gik/core/utils';
import DateTimeUtils from '@gik/core/utils/DateTimeUtils';
import InkindUserUpdateMedia from '@gik/inkind-page/models/InkindUserUpdateMedia';
import { AddUserUpdateInputNames } from '@gik/inkind-page/enums/AddUserUpdateInputNames';

const updateIdHeaderPropertyName = 'location';

export interface IMediaData {
  data?: string;
  url?: string;
  thumbnailUrl?: string;
  caption?: string;
  id?: string;
}

export interface IUserUpdateData {
  title: string;
  description: string;
  publishedDate: string;
}

export async function createUserUpdate(
  routeId: string,
  formData: Partial<IAddEditInkindUserUpdatesForm>,
  media: InkindUserUpdateMedia[]
): Promise<string> {
  let updateId: string;
  try {
    const response = await api.post(`stream/inkinds/${routeId}/user-updates`, {
      json: getUserUpdateTextData(formData),
    });
    updateId = response.headers.get(updateIdHeaderPropertyName);
  } catch (e) {
    console.error('Failed to create user update: ', e);
    return null;
  }

  try {
    await updateUserUpdateMedia(updateId, [], media);
  } catch (e) {
    console.error('Failed to update user update media: ', e);
    return null;
  }

  if (formData[AddUserUpdateInputNames.SEND_OPTIONS_EMAIL]) {
    // Do not await sending email as Vero can take up to 10 seconds to send a successful response
    emailFollowers(updateId);
  }

  return updateId;
}

export async function updateUserUpdate(updateId: string, formData: Partial<IAddEditInkindUserUpdatesForm>) {
  try {
    await api.put(`stream/user-updates/${updateId}`, {
      json: getUserUpdateTextData(formData),
    });
  } catch (e) {
    console.error('Failed to update user update: ', e);
    return;
  }
}

export async function updateUserUpdateMedia(
  updateId: string,
  mediaToBeRemoved: string[],
  media: InkindUserUpdateMedia[]
) {
  for (let i = 0; i < mediaToBeRemoved.length; i++) {
    const mediaId = mediaToBeRemoved[i];

    try {
      await removeMedia(updateId, mediaId);
    } catch (e) {
      console.error('Failed to remove media: ', e);
      return;
    }
  }

  await addMedia(updateId, media);
}

async function addMedia(updateId: string, media: InkindUserUpdateMedia[]) {
  for (let i = 1; i <= media.length; i++) {
    const m = media[i - 1];

    if (m.id) {
      continue;
    }

    if (m.type === 'photo') {
      await addPhoto(updateId, {
        data: m.url,
        caption: m.caption,
      });
    } else {
      await addVideo(updateId, {
        caption: m.caption,
        thumbnailUrl: m.thumbnailUrl,
        url: m.url,
      });
    }
  }
}

export async function addPhoto(updateId: string, photoData: IMediaData) {
  try {
    await api.post(`stream/user-updates/${updateId}/photos`, {
      json: photoData,
    });
  } catch (error) {
    console.error(error);
  }
}

export async function addVideo(updateId: string, videoData: IMediaData) {
  try {
    await api.post(`stream/user-updates/${updateId}/videos`, {
      json: videoData,
    });
  } catch (error) {
    console.error(error);
  }
}

export async function removeMedia(updateId: string, mediaId: string) {
  try {
    await api.delete(`stream/user-updates/${updateId}/media/${mediaId}`);
  } catch (error) {
    console.error(error);
  }
}

export async function removeUpdate(updateId: string) {
  return await api.delete(`stream/user-updates/${updateId}`);
}

export async function emailFollowers(updateId: string) {
  try {
    return await api.post(`stream/user-updates/${updateId}/email-followers`);
  } catch (e) {
    console.error('Failed to email followers: ', e);
    return null;
  }
}
function getUserUpdateTextData(formData: Partial<IAddEditInkindUserUpdatesForm>): IUserUpdateData {
  return {
    title: formData?.[AddUserUpdateInputNames.TITLE],
    description: formData?.[AddUserUpdateInputNames.CONTENT],
    publishedDate: moment(formData?.[AddUserUpdateInputNames.DATE]).format(DateTimeUtils.DateFormat),
  };
}
